import { ContactLeadResponseGateDecisionEnumKey } from '../../../../services/marketplace/generated/kubb/models/ContactLeadResponse';
import {
  ListVsBuyoutBlock,
  ListVsBuyoutBlockTierEnumKey,
} from '../../../../services/marketplace/generated/kubb/models/ListVsBuyoutBlock';
import { formatMoney } from '../../../../utils/helpers';

export type EquityTier = ListVsBuyoutBlockTierEnumKey;
export type GateDecision = ContactLeadResponseGateDecisionEnumKey;

export type EquityRange = { low: number; high: number };

export type BuyoutEstimate = {
  payoff: number;
  taxAndFees: number;
  premium: number;
  premiumOverCap: boolean;
};

export type LeadGuidance = {
  tier: EquityTier;
  equityRange: EquityRange | null;
  mileageOverageEstimate: number | null;
  gateRequired: boolean;
  gateDecision: GateDecision | null;
  payoffVerified: boolean;
  buyout: BuyoutEstimate | null;
  finishLeasePaymentsLeft: number | null;
  sellNowNet: number | null;
  monthlyPayment: number | null;
  remainingMonths: number | null;
};

const toMileageOverageEstimate = (block: ListVsBuyoutBlock): number | null => {
  const { finishLease } = block.branches;
  if (finishLease == null || finishLease.wearAssumptionSensitive) return null;
  if (block.inputs.leaseEndSource == null) return null;
  const overage = finishLease.components.mileageOverage;
  return overage < 0 ? Math.abs(overage) : null;
};

const toBuyoutEstimate = ({ branches }: ListVsBuyoutBlock): BuyoutEstimate | null =>
  branches.buyout != null
    ? {
        payoff: branches.buyout.components.payoff,
        taxAndFees: branches.buyout.components.tax + branches.buyout.components.fees,
        premium: branches.buyout.premium,
        premiumOverCap: branches.buyout.premiumOverCap,
      }
    : null;

export const toLeadGuidance = (
  block: ListVsBuyoutBlock | null | undefined,
  gateDecision: GateDecision | null | undefined,
): LeadGuidance | null => {
  if (block?.tier == null) return null;
  return {
    tier: block.tier,
    equityRange:
      block.equityRange != null
        ? { low: block.equityRange.low, high: block.equityRange.high }
        : null,
    mileageOverageEstimate: toMileageOverageEstimate(block),
    gateRequired: block.gate.required,
    gateDecision: gateDecision ?? null,
    payoffVerified: block.payoff.verified,
    buyout: toBuyoutEstimate(block),
    finishLeasePaymentsLeft:
      block.branches.finishLease != null
        ? Math.abs(block.branches.finishLease.components.payments)
        : null,
    sellNowNet: block.branches.sellNow?.net ?? null,
    monthlyPayment: block.inputs.monthlyPayment,
    remainingMonths: block.inputs.remainingMonths,
  };
};

export type RangeDisplay =
  | { kind: 'range'; low: string; high: string }
  | { kind: 'upTo'; amount: string }
  | { kind: 'none' };

export const MIN_RANGE_DISPLAY_AMOUNT = 500;

// equityRange is signed equity; 'outOfPocket' flips it into what the driver pays.
export const getRangeDisplay = (
  range: EquityRange | null,
  direction: 'toYou' | 'outOfPocket',
): RangeDisplay => {
  if (range == null) return { kind: 'none' };

  const near = direction === 'toYou' ? range.low : -range.high;
  const far = direction === 'toYou' ? range.high : -range.low;
  if (far < MIN_RANGE_DISPLAY_AMOUNT) return { kind: 'none' };

  const money = (value: number) => formatMoney(value, { noCents: true });
  return near < 0
    ? { kind: 'upTo', amount: money(far) }
    : { kind: 'range', low: money(near), high: money(far) };
};

// "About $X" figures round to the nearest $50 so every surface hedges identically.
export const toEstimatedMoney = (value: number): string =>
  formatMoney(Math.round(Math.abs(value) / 50) * 50, { noCents: true });

export const getBuyoutTaxLine = (taxAndFees: number): string =>
  `plus roughly ${toEstimatedMoney(
    taxAndFees,
  )} in estimated taxes and fees depending on your state`;

export type ParkedPath = Exclude<GateDecision, 'list_anyway'>;

export const isParkedPath = (decision: GateDecision | null | undefined): decision is ParkedPath =>
  decision === 'buyout' || decision === 'finish_lease';

export type GateVariant = 'two_way' | 'three_way';

export const getGateVariant = (guidance: LeadGuidance): GateVariant =>
  guidance.buyout?.premiumOverCap ? 'three_way' : 'two_way';

export const hasTierLanes = (tier: EquityTier): boolean =>
  tier === 'thin_positive' || tier === 'negative' || tier === 'deep_negative';

export const shouldShowKeepTheCarCard = (tier: EquityTier): boolean =>
  tier === 'negative' || tier === 'deep_negative';

export const shouldShowGate = (guidance: LeadGuidance): boolean =>
  guidance.gateRequired &&
  guidance.gateDecision == null &&
  guidance.buyout != null &&
  guidance.sellNowNet != null;
